import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { UrlState } from '../models/chart-interfaces';

@Injectable({
  providedIn: 'root'
})
export class ChartUrlStateService {
  private route!: ActivatedRoute;
  private router!: Router;
  private readonly location!: Location;

  constructor(location: Location) {
    this.location = location;
  }

  initialize(route: ActivatedRoute, router: Router): void {
    this.route = route;
    this.router = router;
  }

  /**
   * Get state from URL query parameters
   */
  getStateFromUrl(): UrlState | null {
    if (!this.route) {
      return null;
    }

    const queryParams = this.route.snapshot.queryParams;
    const configParam = queryParams['config'];

    if (!configParam) {
      return null;
    }

    try {
      // Decode Base64 and parse JSON
      const decodedConfig = atob(configParam);
      const parsedConfig = JSON.parse(decodedConfig);

      // Validate and return the state
      return this.validateState(parsedConfig);
    } catch (error) {
      console.warn('Invalid URL configuration:', error);
      return null;
    }
  }

  /**
   * Update URL with current state
   */
  updateUrl(state: UrlState): void {
    if (!this.router) {
      return;
    }

    try {
      // Encode state to Base64
      const configJson = JSON.stringify(state);
      const encodedConfig = btoa(configJson);

      // Update URL without navigation
      const url = this.router.createUrlTree([], {
        relativeTo: this.route,
        queryParams: { config: encodedConfig },
        queryParamsHandling: 'merge'
      });

      this.location.replaceState(this.router.serializeUrl(url));
    } catch (error) {
      console.error('Error updating URL:', error);
    }
  }

  /**
   * Generate a shareable URL for the current state
   */
  generateShareableUrl(state: UrlState): string {
    try {
      const configJson = JSON.stringify(state);
      const encodedConfig = btoa(configJson);

      const baseUrl = window.location.origin + window.location.pathname;
      return `${baseUrl}?config=${encodedConfig}`;
    } catch (error) {
      console.error('Error generating shareable URL:', error);
      return window.location.href;
    }
  }

  /**
   * Validate state object structure
   */
  private validateState(state: any): UrlState | null {
    if (!state || typeof state !== 'object') {
      return null;
    }

    // Required fields validation
    const requiredFields = ['groupBy', 'aggregateBy', 'chartType', 'timeRange'];
    for (const field of requiredFields) {
      if (!(field in state)) {
        console.warn(`Missing required field: ${field}`);
        return null;
      }
    }

    // Valid chart types
    const validChartTypes = ['bar', 'line', 'pie', 'doughnut', 'polarArea'];
    if (!validChartTypes.includes(state.chartType)) {
      console.warn(`Invalid chart type: ${state.chartType}`);
      return null;
    }

    // Valid group by options
    const validGroupBy = ['team', 'repository', 'alertType', 'pipelineType', 'admin', 'systems'];
    if (!validGroupBy.includes(state.groupBy)) {
      console.warn(`Invalid groupBy: ${state.groupBy}`);
      return null;
    }

    // Valid time ranges
    const validTimeRanges = ['P7D', 'P30D', 'P90D', 'P180D', 'P365D'];
    if (!validTimeRanges.includes(state.timeRange)) {
      console.warn(`Invalid timeRange: ${state.timeRange}`);
      return null;
    }

    // Ensure filters object exists
    if (!state.filters) {
      state.filters = {};
    }

    // Ensure chartOptions object exists with defaults
    if (!state.chartOptions) {
      state.chartOptions = {
        stacked: false,
        showLegend: true,
        orientation: 'vertical'
      };
    }

    return state as UrlState;
  }

  /**
   * Clear URL parameters
   */
  clearUrl(): void {
    if (!this.router) {
      return;
    }

    const url = this.router.createUrlTree([], {
      relativeTo: this.route,
      queryParams: {},
    });

    this.location.replaceState(this.router.serializeUrl(url));
  }

  /**
   * Get current URL without query parameters
   */
  getBaseUrl(): string {
    return window.location.origin + window.location.pathname;
  }

  /**
   * Check if current URL has configuration
   */
  hasUrlConfig(): boolean {
    if (!this.route) {
      return false;
    }
    return !!this.route.snapshot.queryParams['config'];
  }

  /**
   * Compress state for shorter URLs (optional enhancement)
   */
  private compressState(state: UrlState): string {
    // For now, just use JSON.stringify
    // Could implement LZ-string compression for complex states
    return JSON.stringify(state);
  }

  /**
   * Decompress state from URL (optional enhancement)
   */
  private decompressState(compressed: string): UrlState {
    // For now, just parse JSON
    // Could implement LZ-string decompression
    return JSON.parse(compressed);
  }

  /**
   * Create a short URL hash for sharing (future enhancement)
   */
  createShortUrl(state: UrlState): Promise<string> {
    // Future implementation could save state to backend and return short hash
    return Promise.resolve(this.generateShareableUrl(state));
  }
}
